import { useContext, type ReactNode } from 'react';
import { NO_VALUE } from '../format.ts';
import { HeaderBar, useStanding, type Reading, type Standing } from './stopped.ts';
import styles from './states.module.css';

/* ==========================================================================
   The instrument, drawn without its readings.

   Not "a loading placeholder". These five shapes are the console's chrome —
   the column headings, the metric labels, the lane rules, the axis grid, the
   row pitch — and every one of those facts is true before the first byte
   arrives, true when the read fails, and true when the read lands with nothing
   in it. So ONE drawing serves all three.

   ---------------------------------------------------------------------------
   THE ONE RULE
   ---------------------------------------------------------------------------
   **NOTHING IN HERE DRAWS A ROW, A CELL OR A BLOCK THAT NOBODY HAS READ.**

   That is stricter than it was for three rounds, and the extra strictness is
   the point. The file used to draw a grid of grey pills at the real pitch
   whenever a request was in flight, on the theory that it previewed the
   layout. On /fleet/list that is 280 pills over 700px — the whole viewport —
   and on /fleet it is four panels of them at once. THE BAR §6 is explicit:
   "Loading: the panel keeps its header and shows a 1px indeterminate bar under
   it... never a skeleton shimmer over a whole page." A ruled grid of 26 empty
   bordered cells is the same promise in a quieter ink, and it is a lie in
   exactly the case where an operator most needs the truth.

   So all five shapes draw the same two things in every state:

     · CHROME THE PANEL KNOWS WITHOUT READING ANYTHING — a table's column
       headings, a rail's field names, a plot's lane labels, thresholds and
       axis, a matrix's metric lanes. Named in the props by the caller, drawn
       for real, with `—` where a single value would have gone.
     · ONE LINE of --text-mid at the first row's position (THE BAR §6),
       supplied by `useStanding`: `Reading…` while a request is in flight,
       `Nothing has been read.` when it failed, and the caller's own absence
       sentence when it landed empty.

   Everything else is ground. An anonymous row is not chrome — nobody knows
   there is a row until the data says so — so no shape here draws one, in any
   state. The panel keeps its box, its heading, its columns and its inset; what
   it stops doing is drawing furniture for data it has not got.

   The difference between "working on it" and "given up" is carried by the 1px
   indeterminate bar under the panel header (`ReadPanel`'s `loading`), by the
   line's words, and by `aria-busy`. That is three channels, none of them
   colour alone, and it is the whole of the vocabulary.
   ========================================================================== */

/* The live region, and the aria-busy that goes with it — but only while a read
   is actually in flight. When the shape is standing in for a read that FAILED,
   or for one that landed empty, a screen reader announcing "Reading rows"
   every poll would be the console lying in the one channel where the lie is
   hardest to catch.
   
   `silent` is the shapes that print a VISIBLE `Reading…` at the first row's
   position: that line is inside a `role="status"` of its own, and announcing
   the same event twice is how a screen-reader user learns to tune the region
   out. The only shape that needs the sr-only copy is the one whose named
   chrome leaves no room for a line — the KPI strip. */
function Busy({ what, mode, silent }: { what: string; mode: Reading['mode']; silent?: boolean }) {
  if (mode !== 'reading' || silent === true) return null;
  return (
    <span className={styles.sr} role="status">
      Reading {what}
    </span>
  );
}

/* THE ONE MOVING THING (THE BAR §6, §7).

   A 1px indeterminate bar across the top edge of the instrument, running only
   while a request is genuinely in flight. It is the channel that separates
   "working on it" from "given up" without spending a word or a colour on it:
   the drawing underneath is identical in both states by design, and until this
   existed the only difference between a console reading the fleet and a
   console that had stopped was one word at the first row's position.

   Positioned absolutely, so it costs the shape no height and arriving data
   moves nothing (THE BAR §7: never animate layout on data arrival). Hidden
   from the accessibility tree — `Busy` and the `Reading…` line already
   announce this event once, and a labelled progressbar beside them would
   announce it twice.

   It is suppressed inside a `ReadPanel`, whose `Panel` header is already
   running the same bar one hairline higher. See `HeaderBar`. */
function Sweep({ mode }: { mode: Reading['mode'] }) {
  const owned = useContext(HeaderBar);
  if (mode !== 'reading' || owned) return null;
  return <span className={styles.sweep} aria-hidden="true" />;
}

/**
 * Props every shape shares. `data-standing` carries the REASON rather than the
 * drawing mode, so a stylesheet can still tell a failed read from an empty
 * one; the drawing is identical either way.
 */
function shell(r: Reading): { 'aria-busy': boolean; 'data-standing': Standing } {
  return { 'aria-busy': r.mode === 'reading', 'data-standing': r.reason };
}

/**
 * The one line, at the first row's position. Rendered by the instrument rather
 * than instead of it — THE BAR §6: "one line of --text-mid text where the
 * first row would be. Nothing else."
 *
 * It is a live region while a read is in flight, so the transition from
 * `Reading…` to `Nothing has been read.` — or to a count of rows — is
 * announced once, in the same channel a sighted operator reads it in.
 */
function Note({ children, live }: { children: ReactNode; live?: boolean }) {
  return (
    <div className={styles.noteRow} {...(live === true ? { role: 'status' } : {})}>
      {children}
    </div>
  );
}

/**
 * THE REST OF THE PANEL, AFTER THE ONE LINE.
 *
 * A settled table draws its column headings and one line of --text-mid, and
 * then — on a route that is one full-height table, /fleet/list and the catalog
 * revision pane — six hundred pixels of bare ground. Flat charcoal at that
 * size does not read as "there are no rows", it reads as a panel that failed
 * to paint, which is the one thing a degraded screen must not look like.
 *
 * So the unused height wears the console's existing mark for deliberate
 * absence: the 45° `--hatch`, the same 1.6% rule a Meter paints when it has no
 * reading and `Awaiting` paints over a stage with no stream. It is two percent
 * of contrast — invisible at arm's length, unmistakable as intent up close —
 * and it draws NOTHING that could be mistaken for a row: no pitch, no cells,
 * no rules. The distinction the last three rounds got wrong is between a
 * DRAWING OF ROWS (a promise) and a marked-out ground (a statement), and this
 * is the second.
 */
function Ground() {
  return <span className={styles.ground} aria-hidden="true" />;
}

/* -- table ----------------------------------------------------------------- */

export interface SkeletonColumn {
  /**
   * The real column heading. Column names are chrome — they are known before
   * the first byte arrives — so a skeleton that greys them out is inventing an
   * absence. Given a label, the header row is drawn for real, and it is the
   * one part of the table that survives a settled read.
   */
  label?: string | undefined;
  /** Any grid/flex width: `2fr`, `120px`. */
  width?: string | undefined;
  /** How much of the column the block fills. 0.6 by default. */
  fill?: number | undefined;
  align?: 'left' | 'right' | undefined;
  /** A dim second line under the value — an id, a timestamp. */
  second?: boolean | undefined;
}

/**
 * A table's instrument: its real column headings, at their real widths, with
 * one line where the first row would be and hatched ground under it.
 *
 * NO ROWS, IN ANY STATE. Not one of them was ever known — a row exists because
 * the data says it does — so a table with no data is its column headings and
 * one line, and that is true while the request is in flight exactly as much as
 * it is true after the request failed. This is what /commands has always done
 * right: `No commands in this window.` and nothing underneath it.
 *
 * `rows` and `edge` are still accepted so callers need not change; they no
 * longer draw anything, because a drawn row is a promise and this shape has
 * nothing to promise.
 */
export function SkeletonTable({
  columns,
  header = true,
  what = 'rows',
}: {
  columns: readonly SkeletonColumn[];
  rows?: number | undefined;
  header?: boolean | undefined;
  /** Accepted for source compatibility; a settled table draws no row edges. */
  edge?: boolean | undefined;
  what?: string | undefined;
}) {
  const standing = useStanding();
  const named = columns.some((c) => c.label !== undefined);
  const reading = standing.mode === 'reading';

  return (
    <div className={styles.skeleton} data-shape="table" {...shell(standing)}>
      <Sweep mode={standing.mode} />
      <Busy what={what} mode={standing.mode} silent={standing.note != null} />
      {/* Headings are the half of the table that is true with no data in it —
          but only the ones the caller actually named. An unnamed column keeps
          its width, so the named ones land where they will land when the rows
          arrive, and prints nothing: a header of grey stubs is the same false
          promise as a row of them. */}
      {header && named && (
        <div className={styles.tHead}>
          {columns.map((c, i) => (
            <div
              key={i}
              className={styles.tCell}
              data-align={c.align ?? 'left'}
              style={{ flex: c.width === undefined ? '1 1 0' : `0 0 ${c.width}` }}
            >
              {c.label !== undefined && <span>{c.label}</span>}
            </div>
          ))}
        </div>
      )}
      {standing.note != null && <Note live={reading}>{standing.note}</Note>}
      <Ground />
    </div>
  );
}

/* -- matrix ---------------------------------------------------------------- */

/**
 * The fleet matrix, with no fleet in it: the named metric lanes and their
 * tracks, and one line above them.
 *
 * `LINK / BATT / SEEN / CERT / REG / MODE` are true whether or not there is a
 * fleet, so they are drawn in every state. A CELL is not — a cell is a reading
 * about one robot — so no cell is drawn until one has been read, in any state.
 * A grid of 168 grey squares was the console claiming it knew the fleet was 28
 * wide before it had read anything at all.
 */
export function SkeletonMatrix({
  labels,
  what = 'the fleet matrix',
}: {
  metrics?: number | undefined;
  /** The real metric names — LINK / MODE / BATT. Chrome, so drawn for real. */
  labels?: readonly string[] | undefined;
  columns?: number | undefined;
  what?: string | undefined;
}) {
  const standing = useStanding();
  const reading = standing.mode === 'reading';
  const lanes = labels ?? [];

  return (
    <div className={styles.matrix} {...shell(standing)}>
      <Sweep mode={standing.mode} />
      <Busy what={what} mode={standing.mode} silent={standing.note != null} />
      {standing.note != null && <Note live={reading}>{standing.note}</Note>}
      {lanes.map((name, m) => (
        <div className={styles.mRow} key={m}>
          <span className={styles.mLabel}>
            <span className={styles.mName}>{name}</span>
          </span>
          <span className={styles.mTrack} />
        </div>
      ))}
    </div>
  );
}

/* -- metric strip ---------------------------------------------------------- */

export interface SkeletonCell {
  /** The real KPI label. */
  label?: string | undefined;
  /** The dim suffix under the figure — `of 30`, `readings`. */
  sub?: string | undefined;
}

/**
 * A KPI strip at a fixed height, so the row does not grow when it reads.
 *
 * A named cell keeps its label and prints an em dash where the figure goes —
 * THE BAR §3: a missing value is grey and explicit, never a zero and never a
 * blank, and never a `•••` either. An unnamed cell has nothing true to say and
 * is not drawn.
 *
 * This is the one shape with no room for a line: eight labelled cells across
 * 1600px have no first-row position to put one at, and the em dashes already
 * say the figure is not in yet. So it keeps the sr-only live region instead.
 */
export function SkeletonStrip({
  cells = 6,
  what = 'fleet totals',
}: {
  cells?: number | readonly SkeletonCell[] | undefined;
  what?: string | undefined;
}) {
  const standing = useStanding();
  const reading = standing.mode === 'reading';
  const spec: readonly SkeletonCell[] =
    typeof cells === 'number' ? Array.from({ length: cells }, () => ({})) : cells;
  const named = spec.filter((c) => c.label !== undefined);
  /* Nothing named: there is no strip to keep, so the band says its one line
     rather than collapsing to a 0px box and taking the row's height out of the
     layout it exists to hold open. */
  const mute = named.length === 0;

  return (
    <div className={styles.strip} {...shell(standing)}>
      <Sweep mode={standing.mode} />
      <Busy what={what} mode={standing.mode} silent={!mute ? false : standing.note != null} />
      {mute && standing.note != null && <Note live={reading}>{standing.note}</Note>}
      {named.map((c, i) => (
        <div className={styles.sCell} key={i}>
          <span className={styles.sLabel}>{c.label}</span>
          <span className={styles.sVoid}>{NO_VALUE}</span>
          {c.sub !== undefined && <span className={styles.sSub}>{c.sub}</span>}
        </div>
      ))}
    </div>
  );
}

/* -- lane plot ------------------------------------------------------------- */

export interface SkeletonLane {
  /** The channel: `Battery`, `CPU`, `Link RSSI`. */
  label: string;
  /** The unit, dimmer, under the label — `%`, `°C`, `dBm`. */
  unit?: string | undefined;
  /**
   * The threshold rules this channel is judged against, as a fraction of the
   * lane measured from its top plus the figure printed at the rule. They are
   * chrome — the limit does not move because the trace is missing — so they
   * stay drawn when there is nothing to plot.
   */
  rules?: readonly { at: number; label: string }[] | undefined;
}

/**
 * A lane plot. A NAMED lane is an instrument: its label, its unit, its grid
 * and its threshold rules are all true with no trace on them, so they are
 * drawn in every state and the operator reads "nothing was reported" against
 * the same limits they judge a live channel by. That is the one shape in this
 * file that legitimately keeps its full height with no data in it.
 *
 * An unnamed lane is not an instrument, it is a placeholder, and it is never
 * drawn — not while reading either. Four grey stubs stacked in a plot is the
 * console guessing at how many channels the robot has.
 */
export function SkeletonPlot({
  lanes = 4,
  ticks = 6,
  what = 'telemetry',
}: {
  lanes?: number | readonly SkeletonLane[] | undefined;
  ticks?: number | readonly string[] | undefined;
  what?: string | undefined;
}) {
  const standing = useStanding();
  const reading = standing.mode === 'reading';
  const spec: readonly (SkeletonLane | null)[] =
    typeof lanes === 'number' ? Array.from({ length: lanes }, () => null) : lanes;
  const axis: readonly (string | null)[] =
    typeof ticks === 'number' ? Array.from({ length: ticks }, () => null) : ticks;
  const shown = spec.filter((l): l is SkeletonLane => l !== null);
  const axisShown = axis.filter((t): t is string => t !== null);

  return (
    <div className={styles.plot} {...shell(standing)}>
      <Sweep mode={standing.mode} />
      <Busy what={what} mode={standing.mode} silent={standing.note != null} />
      {/* Nothing named, nothing to plot: the line stands on its own rather than
          inside an empty box of grid. */}
      {shown.length === 0 && standing.note != null && <Note live={reading}>{standing.note}</Note>}
      {shown.map((lane, i) => (
        <div className={styles.pLane} key={i}>
          <div className={styles.pGutter}>
            <span className={styles.pName}>{lane.label}</span>
            {lane.unit !== undefined && <span className={styles.pUnit}>{lane.unit}</span>}
          </div>
          <div className={styles.pField}>
            {(lane.rules ?? []).map((r, k) => (
              <span
                className={styles.pRule}
                key={k}
                data-side={k % 2 === 0 ? 'right' : 'left'}
                style={{ top: `${Math.round(r.at * 100)}%` }}
                aria-hidden="true"
              >
                <span className={styles.pRuleLabel}>{r.label}</span>
              </span>
            ))}
            {standing.note != null && i === 0 && (
              <span className={styles.pNote} {...(reading ? { role: 'status' } : {})}>
                {standing.note}
              </span>
            )}
          </div>
        </div>
      ))}
      {axisShown.length > 0 && (
        <div className={styles.pAxis} aria-hidden="true">
          {axisShown.map((t, i) => (
            <span key={i} className={styles.pTick}>
              {t}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/* -- label/value rail ------------------------------------------------------ */

/**
 * A label/value rail. Values right-aligned, where the figures will land.
 *
 * A NAMED field is chrome: `Revision`, `Board`, `Cert serial` are facts this
 * object HAS whether or not the read landed, so the rail keeps every one of
 * them and prints `—` against it in every state. That is the densest honest
 * degraded state in the console and it is why /fleet/detail survives an outage
 * intact.
 *
 * An UNNAMED row is not chrome — it is a guess at how many fields there might
 * be — so it is never drawn. Seven grey stubs each carrying a lone em dash,
 * for a morphology that does not exist, is the console inventing an object.
 */
export function SkeletonRail({
  labels,
  what = 'identity',
}: {
  rows?: number | undefined;
  labels?: readonly string[] | undefined;
  what?: string | undefined;
}) {
  const standing = useStanding();
  const reading = standing.mode === 'reading';
  const names = labels ?? [];

  return (
    <div className={styles.rail} {...shell(standing)}>
      <Sweep mode={standing.mode} />
      <Busy what={what} mode={standing.mode} silent={standing.note != null} />
      {standing.note != null && <Note live={reading}>{standing.note}</Note>}
      {names.map((name, i) => (
        <div className={styles.rRow} key={i}>
          <span className={styles.rName}>{name}</span>
          <span className={styles.rVoid}>{NO_VALUE}</span>
        </div>
      ))}
    </div>
  );
}
